import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge, Button, Field, Mono, Select } from "./Field";

type Collection = { id: string; key: string; name: string; name_plural: string };
type Entry = { id: string; slug: string; status: string };

export function EntryPicker({ siteSlug, open, onPick, onClose, collectionKey }: { siteSlug: string; open: boolean; onPick: (e: { id: string; slug: string; collectionKey: string }) => void; onClose: () => void; collectionKey?: string }) {
  const [cols, setCols] = useState<Collection[]>([]);
  const [selCol, setSelCol] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get<{ items: Collection[] }>(`/api/sites/${siteSlug}/collections`).then((r) => {
      setCols(r.items);
      const initial = collectionKey ? r.items.find((c) => c.key === collectionKey)?.id ?? r.items[0]?.id ?? null : r.items[0]?.id ?? null;
      setSelCol(initial);
    });
  }, [open, siteSlug, collectionKey]);

  useEffect(() => {
    if (!open || !selCol) return;
    api.get<{ items: Entry[] }>(`/api/sites/${siteSlug}/collections/${selCol}/entries${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setEntries(r.items));
  }, [open, siteSlug, selCol, q]);

  if (!open) return null;
  const colDef = cols.find((c) => c.id === selCol);
  return (
    <div
      className="fixed inset-0 bg-[rgba(12,13,14,0.55)] backdrop-blur-[2px] z-[1000] grid place-items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--color-paper)] border border-[var(--color-rule)] rounded-[var(--radius-md)] shadow-[0_24px_60px_-12px_rgba(12,13,14,0.35)] w-full max-w-[640px] max-h-[88vh] flex flex-col reveal reveal-1"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-rule)]">
          <div>
            <div className="section-no mb-1">№ Picker · Entry</div>
            <h2 className="display text-2xl text-[var(--color-ink)]">Pick an <em>entry</em></h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </header>
        <div className="px-6 pt-5">
          <Select label="Collection" value={selCol ?? ""} onChange={(e) => setSelCol(e.target.value)}>
            {cols.map((c) => <option key={c.id} value={c.id}>{c.name_plural}</option>)}
          </Select>
          <Field label="Search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="slug" />
        </div>
        <div className="px-6 pb-6 overflow-auto flex-1">
          {entries.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[var(--color-muted)]">No entries.</div>
          ) : (
            <ul className="divide-y divide-[var(--color-rule)] border-y border-[var(--color-rule)]">
              {entries.map((e) => (
                <li key={e.id}>
                  <button
                    onClick={() => { if (colDef) onPick({ id: e.id, slug: e.slug, collectionKey: colDef.key }); onClose(); }}
                    className="w-full text-left bg-transparent border-0 cursor-pointer py-3 px-2 flex items-center justify-between gap-3 hover:bg-[var(--color-paper-2)] transition-colors"
                  >
                    <Mono className="text-[13px] text-[var(--color-ink)]">/{e.slug}</Mono>
                    <Badge tone={e.status === "published" ? "success" : e.status === "review" ? "warning" : "neutral"}>{e.status}</Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
